// @flow
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { Link } from 'gatsby'
import * as React from 'react'

import { colors, media } from '@toggl/style'

import { useHasMounted } from '../hooks/useHasMounted'
import { IconArrowBigLeft } from '../icons/IconArrow'
import { IconFacebook } from '../icons/IconFacebook'
import { IconLinkedIn } from '../icons/IconLinkedIn'
import { IconTwitter } from '../icons/IconTwitter'
import * as text from '../text'

declare var globalThis: any

type Props = {
  goBackLink?: string,
  title: string | React.Node,
  socialText?: string,
  backText?: string,
  urlToShare?: string,
}

export const BackSocial = ({
  goBackLink,
  title,
  socialText,
  backText,
  urlToShare,
}: Props): null | React.Element<typeof Root> => {
  const theme = useTheme()
  const hasMounted = useHasMounted()

  if (!hasMounted) {
    return null
  }

  return (
    <Root>
      <Header>
        {backText && (
          <BackWrapper>
            <LinkStyled to={goBackLink}>
              <IconArrowBigLeft color={theme.primary} />

              <HeaderText>{backText}</HeaderText>
            </LinkStyled>
          </BackWrapper>
        )}
        <DesktopSubHeaderContent>
          <SocialIcons socialText={socialText} urlToShare={urlToShare} />
        </DesktopSubHeaderContent>
      </Header>

      <MobileSubHeaderContent>
        {typeof title === 'string' && <PositionTitle>{title}</PositionTitle>}
        {typeof title !== 'string' && title}

        <SocialIcons socialText={socialText} urlToShare={urlToShare} />
      </MobileSubHeaderContent>
    </Root>
  )
}

const Root = styled<{ children: React.Node }>('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  background-color: ${colors.darkPurple};

  ${media.mq[1]} {
    padding: 64px 122px 49px 122px;
    flex-direction: row;
  }
`

const SocialIcons = ({
  socialText,
  urlToShare,
}: {
  socialText?: string,
  urlToShare?: string,
}) => {
  const shareUrl = urlToShare ?? 'https://toggl.com/jobs'

  return (
    <SocialIconsStyled>
      <P>{socialText || 'Share this job'}</P>

      <div>
        <Link
          target="_blank"
          href={getTwitterLink(
            'Great news! @toggl is hiring remotely for your dream job! Check it out:',
            shareUrl
          )}
        >
          <IconTwitter />
        </Link>

        <Link target="_blank" href={getFacebookLink(shareUrl)}>
          <IconFacebook />
        </Link>

        <Link
          target="_blank"
          href={getLinkedinLink(
            'Toggl is hiring remotely for your dream job!',
            shareUrl
          )}
        >
          <IconLinkedIn />
        </Link>
      </div>
    </SocialIconsStyled>
  )
}

const H = styled.h2`
  ${text.heading2};
  color: ${colors.beige};
`

const P = styled.span`
  ${text.paragraph3};
  color: ${colors.beige};
`

const LinkStyled = styled(Link)`
  svg {
    width: 32px;
  }

  &:hover span {
    color: ${(p) => p.theme.primary};
  }
`

const PositionTitle = styled(H)`
  width: 249px;
  text-align: center;
  margin-top: 13px;
`

const SocialIconsStyled = styled.div`
  margin-top: 40px;

  div {
    display: flex;
    justify-content: space-between;
    margin-top: 13px;
    min-width: 5rem;
  }

  a:hover svg {
    fill: ${(p) => p.theme.primary};
  }

  ${media.mq[1]} {
    margin-top: 0;
    margin-left: auto;
    display: flex;
    justify-content: space-between;

    div {
      margin: 0;
    }

    > span {
      margin-right: 25px;
    }

    > div {
      display: flex;
      align-items: center;
    }
  }
`

const DesktopSubHeaderContent = styled.div`
  display: none;

  ${media.mq[1]} {
    display: flex;
  }
  flex-direction: column;
`

const MobileSubHeaderContent = styled.div`
  background-color: ${colors.darkPurple};
  padding-top: 26px;

  display: flex;
  flex-direction: column;
  align-items: center;

  ${media.mq[1]} {
    display: none;
  }
`

const HeaderText = styled(P)`
  margin-left: 20px;
  color: ${colors.beige};
`

const Header = styled.div`
  width: 100%;

  ${media.mq[1]} {
    display: flex;
    justify-content: space-between;
  }
`

const BackWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 27px 23px;
  border-bottom: 1px solid ${colors.fadedPurple};

  ${media.mq[1]} {
    border-bottom: none;
    padding: 0;
    justify-content: space-between;
  }
`

const getTwitterLink = (text: string, url: string) => {
  const twitterLink = new URL('https://twitter.com/intent/tweet')
  twitterLink.searchParams.set('text', text)
  twitterLink.searchParams.set('url', url)
  return twitterLink.href
}

const getFacebookLink = (url: string) => {
  const facebookLink = new URL('https://www.facebook.com/sharer/sharer.php')
  facebookLink.searchParams.set('u', url)
  return facebookLink.href
}

const getLinkedinLink = (text: string, url: string) => {
  const linkedinLink = new URL('https://www.linkedin.com/shareArticle')
  linkedinLink.searchParams.set('mini', 'true')
  linkedinLink.searchParams.set('title', text)
  linkedinLink.searchParams.set('url', url)
  return linkedinLink.href
}
